<template>
  <material-card
    :title="$t('components.plan_builder.unions_import_wizard.title')"
    :color="$vuetify.theme.themes.light.secondary"
    icon="publish"
  >
    <v-card-text>
      <v-dialog v-model="dialog" persistent max-width="400">
        <v-card>
          <v-card-title class="headline">
            {{ $t("globals.cancel_wizard") }}
          </v-card-title>
          <v-card-text>
            {{
              $t(
                "components.inmemorystores.if_you_exit_the_wizard_any_changes_made_so_far"
              )
            }}
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="dialog = false">
              {{ $t("globals.keep_working") }}
            </v-btn>
            <v-btn color="primary" @click="cancel()">
              {{ $t("globals.cancel_anyway") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-subheader class="pa-0">
        {{
          $t(
            "components.plan_builder.unions_import_wizard.you_are_few_steps_away_from_importing_unions"
          )
        }}
      </v-subheader>
      <v-stepper v-model="step" vertical class="elevation-0">
        <v-stepper-step :complete="step > 1" :step="1">
          {{
            $t(
              "components.membersimportwizardcomponent.download_data_template_optional"
            )
          }}
          <small>
            {{
              $t(
                "components.membersimportwizardcomponent.download_and_use_a_data_template_in_xlsx_format_you"
              )
            }}
          </small>
        </v-stepper-step>
        <v-stepper-content :step="1">
          <v-container fluid>
            <v-row fluid>
              <v-col class="col-auto">
                <v-menu offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      slot="activator"
                      color="success"
                      :loading="downloading"
                      :disabled="downloading"
                      v-on="on"
                    >
                      {{
                        $t("globals.download")
                      }}
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="downloadTemplate(false)">
                      <v-list-item-title>
                        {{
                          $t("components.membersimportwizardcomponent.empty")
                        }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="downloadTemplate(true)">
                      <v-list-item-title>
                        {{
                          $t(
                            "components.membersimportwizardcomponent.all_members"
                          )
                        }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>
              <v-col class="col-auto">
                <v-btn color="primary" @click="step = 2">
                  {{ $t("globals.continue") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-stepper-content>
        <v-stepper-step :complete="step > 2" :step="2">
          {{ $t("components.plan_builder.union_import_wizard.upload_unions") }}
          <small>
            {{
              $t(
                "components.plan_builder.employers_import_wizard.choose_the_file_to_import_data_from_using_the_select"
              )
            }}
          </small>
        </v-stepper-step>
        <v-stepper-content :step="2">
          <v-row>
            <v-col cols="12" md="6">
              <div>
                <v-file-input
                  show-size
                  accept=".xlsx"
                  ref="file"
                  v-on:change="handleUnionsChange"
                />
              </div>
            </v-col>
          </v-row>
          <span v-if="uploading">
            {{ $t("components.membersimportwizardcomponent.please_wait") }}
          </span>
          <div v-if="uploaded && validationErrors.length === 0">
            <v-banner
              icon="done"
              color="success"
              elevation="0"
              icon-color="white"
            >
              {{ uploadResult }}
            </v-banner>
          </div>
          <template v-for="(error, i) in validationErrors">
            <v-alert dense type="error" :key="i">{{ error }}</v-alert>
          </template>
          <v-card-actions>
            <v-btn :disabled="uploading" text @click="step = 1">
              {{ $t("globals.back") }}
            </v-btn>
            <v-col class="col-auto">
              <v-btn
                :disabled="!uploaded || validationErrors.length > 0"
                color="primary"
                @click="step = 3"
              >
                {{ $t("globals.continue") }}
              </v-btn>
            </v-col>
          </v-card-actions>
        </v-stepper-content>
        <v-stepper-step :complete="step > 3" :step="3">
          {{
            $t(
              "components.plan_builder.unions_import_wizard.upload_union_locals"
            )
          }}
          <small>
            {{
              $t(
                "components.plan_builder.employers_import_wizard.choose_the_file_to_import_data_from_using_the_select"
              )
            }}
          </small>
        </v-stepper-step>
        <v-stepper-content :step="3">
          <v-row>
            <v-col cols="12" md="6">
              <div>
                <v-file-input
                  show-size
                  accept=".xlsx"
                  ref="file"
                  v-on:change="handleLocalsChange"
                />
              </div>
            </v-col>
          </v-row>
          <span v-if="uploading">
            {{ $t("components.membersimportwizardcomponent.please_wait") }}
          </span>
          <div v-if="uploaded && validationErrors.length === 0">
            <v-banner
              icon="done"
              color="success"
              elevation="0"
              icon-color="white"
            >
              {{ uploadResult }}
            </v-banner>
          </div>
          <template v-for="(error, i) in validationErrors">
            <v-alert dense type="error" :key="i">{{ error }}</v-alert>
          </template>
          <v-card-actions>
            <v-btn :disabled="uploading" text @click="step = 2">
              {{ $t("globals.back") }}
            </v-btn>
            <v-col class="col-auto">
              <v-btn
                :disabled="!uploaded || validationErrors.length > 0"
                color="primary"
                @click="step = 4"
              >
                {{ $t("globals.continue") }}
              </v-btn>
            </v-col>
          </v-card-actions>
        </v-stepper-content>
        <v-stepper-step :complete="step > 4" :step="4">
          {{
            $t(
              "components.plan_builder.union_import_wizard.upload_union_locals_contacts"
            )
          }}
          <small>
            {{
              $t(
                "components.plan_builder.employers_import_wizard.choose_the_file_to_import_data_from_using_the_select"
              )
            }}
          </small>
        </v-stepper-step>
        <v-stepper-content :step="4">
          <v-row>
            <v-col cols="12" md="6">
              <div>
                <v-file-input
                  show-size
                  accept=".xlsx"
                  ref="file"
                  v-on:change="handleLocalsContactsChange"
                />
              </div>
            </v-col>
          </v-row>
          <span v-if="uploading">
            {{ $t("components.membersimportwizardcomponent.please_wait") }}
          </span>
          <div v-if="uploaded && validationErrors.length === 0">
            <v-banner
              icon="done"
              color="success"
              elevation="0"
              icon-color="white"
            >
              {{ uploadResult }}
            </v-banner>
          </div>
          <template v-for="(error, i) in validationErrors">
            <v-alert dense type="error" :key="i">{{ error }}</v-alert>
          </template>
          <v-card-actions>
            <v-btn :disabled="uploading" text @click="step = 3">
              {{ $t("globals.back") }}
            </v-btn>
            <v-col class="col-auto">
              <v-btn
                :disabled="!uploaded || validationErrors.length > 0"
                color="primary"
                @click="step = 5"
              >
                {{ $t("globals.continue") }}
              </v-btn>
            </v-col>
          </v-card-actions>
        </v-stepper-content>
      </v-stepper>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions>
      <v-btn
        :disabled="uploading"
        text
        @click="dialog = true"
      >
        {{ $t("globals.cancel") }}
      </v-btn>
      <v-btn
        color="primary"
        @click="finish"
        :disabled="validationErrors.length > 0"
      >
        {{ $t("globals.finish") }}
      </v-btn>
    </v-card-actions>
    <error-handler :error="error"></error-handler>
  </material-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ErrorHandler from "@/components/ErrorHandler";
import * as ExcelJS from "exceljs";

export default {
  components: {
    ErrorHandler,
  },
  data() {
    return {
      error: null,
      step: 1,
      dialog: false,
      uploaded: false,
      uploading: false,
      uploadResult: "",
      downloading: false,
      unions: [],
      locals: [],
      validationErrors: [],
      unionsColumnsMatches: {
        Name: "Name",
        "Trade Code": "TradeCode",
      },
      localsColumnsMatches: {
        Name: "Name",
        Number: "Number",
        Union: "Union",
        Address: "Address",
        Telephone: "PhoneNumber",
        "Fax Number": "Fax",
      },
      unionLocalsContactsColumnsMatches: {
        Name: "Name",
        Email: "Email",
        Principal: "IsPrincipal",
        "Union Local": "UnionLocal",
      },
    };
  },
  computed: {
    ...mapGetters("common", {
      settings: "settings",
      selectedPlan: "plan",
    }),
    ...mapGetters("plansMembers", ["options"]),
  },
  watch: {
    step: {
      handler(v) {
        switch (v) {
          case 1: {
            break;
          }
          default: {
            this.uploaded = false;
            this.uploadResult = "";
            this.uploading = false;
            break;
          }
        }
      },
    },
  },
  methods: {
    ...mapActions("plansMembers", ["getBlobAsync"]),
    handleUnionsChange(file) {
      if (!file) {
        this.file = null;
        return;
      }
      if (file.length == 0) {
        return;
      }
      this.onUploadStarted();
      var reader = new FileReader();
      reader.onload = async (event) => {
        var data = event.target.result;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(data);
        var worksheet = workbook.getWorksheet("Data");
        // var worksheet = workbook.getWorksheet(0);
        const headers = [];
        const unions = [];

        worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          console.log(row);
          console.log("Row " + rowNumber + " = " + JSON.stringify(row.values));
          if (rowNumber === 1) {
            for (const v of row.values) {
              const match = this.unionsColumnsMatches[v];
              // if (match!==null && match !==undefined) {
              headers.push(match);
              // }
            }
            console.log(headers);
          } else {
            const jsonRow = {
              Identifier: Math.random(),
            };
            for (let i = 0; i < row.values.length; i++) {
              jsonRow[headers[i]] = row.values[i];

              if (jsonRow[headers[i]] === "") {
                jsonRow[headers[i]] = null;
              }
            }
            unions.push(jsonRow);
          }
        });
        this.validateUnions(unions);
        this.onUploadCompleted();
      };
      reader.onerror = () => {
        this.onUploadError(reader.error);
      };
      reader.readAsBinaryString(file);
    },
    validateUnions(unions) {
      this.validationErrors = [];
      for (let i = 0; i < unions.length; i++) {
        if (unions[i].Name === null || unions[i].Name === undefined) {
          this.validationErrors.push(
            this.$t(
              `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Name is required`,
              [i + 2, "Union"]
            )
          );
        } else {
          if (!this.isUniqueUnionName(i, unions)) {
            this.validationErrors.push(
              this.$t(
                `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Name must be unique`,
                [i + 2, "Union"]
              )
            );
          }
        }
        // if (unions[i].TradeCode === null || unions[i].TradeCode === undefined) {
        //   this.validationErrors.push(
        //     this.$t(
        //       `components.plan_builder.unions_import_wizard.errors.Row {0}: {1}'s Trade Code is required`,
        //       [i + 2, "Union"]
        //     )
        //   );
        // } else {
        if (unions[i].TradeCode !== null && unions[i].TradeCode !== undefined) {
          if (!this.isNumeric(unions[i].TradeCode)) {
            this.validationErrors.push(
              this.$t(
                `components.plan_builder.unions_import_wizard.errors.Row {0}: {1}'s Trade Code must be numeric`,
                [i + 2, "Union"]
              )
            );
          }
          if (!this.isLength(1, unions[i].TradeCode)) {
            this.validationErrors.push(
              this.$t(
                `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Trade Code must be exactly 1 digit`,
                [i + 2, "Union"]
              )
            );
          }
          if (!this.isUniqueUnionTradeCode(i, unions)) {
            this.validationErrors.push(
              this.$t(
                `components.plan_builder.unions_import_wizard.errors.Row {0}: {1}'s Trade Code must be unique`,
                [i + 2, "Union"]
              )
            );
          }
        }
      }

      if (this.validationErrors.length === 0) {
        this.unions = unions.map((o) => {
          delete o.undefined;
          return o;
        });
      }
      console.log(this.unions);
    },
    // parseAddress(item, index, entity) {
    //   const parts = item.Address.split(",").map((o) => o.trim());

    //   if (parts.length !== 4) {
    //     this.validationErrors.push(
    //       this.$t(
    //         `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Address not in valid format`,
    //         [index + 2, entity]
    //       )
    //     );
    //     return undefined;
    //   }

    //   const streetNumberParts = parts[0].split(" ");

    //   let suiteNumber;
    //   let streetNumber;
    //   let validStreetNumber = false;
    //   const validSuiteNumber = streetNumberParts.length > 1;
    //   if (validSuiteNumber) {
    //     suiteNumber = "";
    //     for (let i = 0; i < streetNumberParts.length - 1; i++) {
    //       suiteNumber = suiteNumber + " " + streetNumberParts[i];
    //     }
    //     suiteNumber = suiteNumber.trim();
    //     streetNumber = streetNumberParts[streetNumberParts.length - 1];
    //     validStreetNumber = this.isStreetNumber(streetNumber);
    //     if (!validStreetNumber) {
    //       this.validationErrors.push(
    //         this.$t(
    //           `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Address/Street Number not in valid format`,
    //           [index + 2, entity]
    //         )
    //       );
    //     }
    //   } else {
    //     if (streetNumberParts.length === 0) {
    //       this.validationErrors.push(
    //         this.$t(
    //           `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Address/Street Number is required`,
    //           [index + 2, entity]
    //         )
    //       );
    //     } else {
    //       streetNumber = streetNumberParts[0];
    //       validStreetNumber = this.isStreetNumber(streetNumber);
    //       if (!validStreetNumber) {
    //         this.validationErrors.push(
    //           this.$t(
    //             `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Address/Street Number not in valid format`,
    //             [index + 2, entity]
    //           )
    //         );
    //       }
    //     }
    //   }
    //   const provinceParts = parts[parts.length - 1].split(" ");
    //   const validProvince =
    //     this.options.Models.Province.filter((o) => o.Code === provinceParts[0])
    //       .length > 0;
    //   if (!validProvince) {
    //     this.validationErrors.push(
    //       this.$t(
    //         `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Address/Province not in valid format`,
    //         [index + 2, entity]
    //       )
    //     );
    //   }
    //   let validPostalCode = provinceParts.length === 3;
    //   let postalCode;
    //   if (!validPostalCode) {
    //     this.validationErrors.push(
    //       this.$t(
    //         `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Address/Postal Code not in valid format`,
    //         [index + 2, entity]
    //       )
    //     );
    //   } else {
    //     postalCode = this.unmask(
    //       provinceParts[1] + " " + provinceParts[2],
    //       this.postalCodeMask
    //     );
    //     console.log(postalCode);
    //     validPostalCode = this.isPostalCode(postalCode);
    //     if (!validPostalCode) {
    //       this.validationErrors.push(
    //         this.$t(
    //           `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Address/Postal Code not in valid format`,
    //           [index + 2, entity]
    //         )
    //       );
    //     }
    //   }

    //   if (validStreetNumber && validProvince && validPostalCode) {
    //     return {
    //       StreetNumber: streetNumber,
    //       StreetName: parts[1],
    //       SuiteNumber: suiteNumber,
    //       City: parts[2],
    //       Province: this.options.Models.Province.filter(
    //         (o) => o.Code === provinceParts[0]
    //       )[0],
    //       PostalCode: postalCode,
    //       Country: this.options.Models.Country.filter((o) => !o.IsOther)[0],
    //     };
    //   }
    //   return undefined;
    // },
    parseAddress(item, index, entity) {
      const parts = item.Address.split(",").map((o) => o.trim());

      if (parts.length < 4) {
        this.validationErrors.push(
          this.$t(
            `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Address not in valid format`,
            [index + 2, entity]
          )
        );
        return undefined;
      }

      const streetParts = parts[0].split(" ");

      let suiteNumber;
      let streetNumber;
      let streetName;
      let city;
      let province;
      let postalCode;
      let validStreetNumber = false;
      let validProvince = false;
      let validPostalCode = false;

      if (streetParts.length === 0) {
        this.validationErrors.push(
          this.$t(
            `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Address/Street Number is required`,
            [index + 2, entity]
          )
        );
      } else {
        streetNumber = streetParts[0];
        validStreetNumber = this.isStreetNumber(streetNumber);
        if (!validStreetNumber) {
          this.validationErrors.push(
            this.$t(
              `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Address/Street Number not in valid format`,
              [index + 2, entity]
            )
          );
        }

        if (streetParts.length < 2) {
          this.$t(
            `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Address/Street Name is required`,
            [index + 2, entity]
          );
        } else {
          streetName = "";
          for (let i = 1; i < streetParts.length; i++) {
            streetName += streetParts[i] + " ";
          }
        }
      }

      if (parts.length === 5) {
        //has suite number
        suiteNumber = parts[1];
        city = parts[2];
        province = parts[3];
        postalCode = parts[4];
      } else {
        city = parts[1];
        province = parts[2];
        postalCode = parts[3];
      }
      streetName = streetName.trim();
      validProvince =
        this.options.Models.Province.filter((o) => o.Code === province).length >
        0;
      if (!validProvince) {
        this.validationErrors.push(
          this.$t(
            `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Address/Province not in valid format`,
            [index + 2, entity]
          )
        );
      }
      validPostalCode = this.isPostalCode(postalCode);
      if (!validPostalCode) {
        this.validationErrors.push(
          this.$t(
            `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Address/Postal Code not in valid format`,
            [index + 2, entity]
          )
        );
      }

      if (validStreetNumber && validProvince && validPostalCode) {
        return {
          StreetNumber: streetNumber,
          StreetName: streetName,
          SuiteNumber: suiteNumber,
          City: city,
          Province: this.options.Models.Province.filter(
            (o) => o.Code === province
          )[0],
          PostalCode: postalCode,
          Country: this.options.Models.Country.filter((o) => !o.IsOther)[0],
        };
      }
      return undefined;
    },
    isNumeric(v) {
      return /^[0-9]*$/.test(v);
    },
    isLength(length, v) {
      var parts = v.toString().split("");
      return parts.length === length;
    },
    isUpToLength(length, v) {
      var parts = v.toString().split("");
      return parts.length <= length;
    },
    isFiveZeros(v) {
      return v === "00000";
    },
    isPhoneNumber(v) {
      // return /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(v);
      return /^[0-9]{10}$/.test(v);
    },
    isEmail(v) {
      return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        String(v).toLowerCase()
      );
    },
    isStreetNumber(v) {
      return /^[a-zA-Z0-9]+([(\-| )][a-zA-Z0-9]+)*$/.test(v);
    },
    isPostalCode(v) {
      return /^(?!.*[DFIOQUdfioqu])[A-VXYa-vxy][0-9][A-Za-z] ?[0-9][A-Za-z][0-9]$/.test(
        v
      );
    },
    isUniqueUnionName(index, employers) {
      for (let i = 0; i < employers.length; i++) {
        if (index !== i) {
          if (employers[i].Name === employers[index].Name) {
            return false;
          }
        }
      }
      return true;
    },
    isUniqueUnionTradeCode(index, employers) {
      for (let i = 0; i < employers.length; i++) {
        if (index !== i) {
          if (employers[i].Name === employers[index].Name) {
            return false;
          }
        }
      }
      return true;
    },
    handleLocalsChange(file) {
      if (!file) {
        this.file = null;
        return;
      }
      if (file.length == 0) {
        return;
      }
      this.onUploadStarted();
      var reader = new FileReader();
      reader.onload = async (event) => {
        var data = event.target.result;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(data);
        var worksheet = workbook.getWorksheet("Data");
        const headers = [];
        let locals = [];
        worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          if (rowNumber === 1) {
            for (const v of row.values) {
              headers.push(this.localsColumnsMatches[v]);
            }
          } else {
            const jsonRow = {
              Identifier: Math.random(),
            };
            for (let i = 0; i < row.values.length; i++) {
              if (headers[i] === "PhoneNumber" || headers[i] === "Fax") {
                if (row.values[i] !== null && row.values[i] !== undefined) {
                  jsonRow[headers[i]] = row.values[i].toString();
                }
              } else {
                jsonRow[headers[i]] = row.values[i];
              }

              if (jsonRow[headers[i]] === "") {
                jsonRow[headers[i]] = null;
              }
            }
            locals.push(jsonRow);
          }
        });
        this.validateLocals(locals);
        if (this.validationErrors.length === 0) {
          this.locals = locals.map((o) => {
            delete o.undefined;
            return o;
          });
        }
        this.onUploadCompleted();
      };
      reader.onerror = () => {
        this.onUploadError(reader.error);
      };
      reader.readAsBinaryString(file);
    },
    validateLocals(locals) {
      this.validationErrors = [];
      for (let i = 0; i < locals.length; i++) {
        const union = this.unions.filter((o) => o.Name === locals[i].Union);
        if (union.length === 0) {
          this.validationErrors.push(
            this.$t(
              `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Union does not exist`,
              [i + 2, "Union Local"]
            )
          );
        }
        if (locals[i].Name === null || locals[i].Name === undefined) {
          this.validationErrors.push(
            this.$t(
              `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Name is required`,
              [i + 2, "Union Local"]
            )
          );
        }
        if (locals[i].Number === null || locals[i].Number === undefined) {
          this.validationErrors.push(
            this.$t(
              `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Number is required`,
              [i + 2, "Union Local"]
            )
          );
        } else {
          if (!this.isNumeric(locals[i].Number)) {
            this.validationErrors.push(
              this.$t(
                `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Number must be numeric`,
                [i + 2, "Union Local"]
              )
            );
          }
          if (!this.isUpToLength(5, locals[i].Number)) {
            this.validationErrors.push(
              this.$t(
                `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Number must be up to 5 digits`,
                [i + 2, "Union Local"]
              )
            );
          }
          if (this.isFiveZeros(locals[i].Number)) {
            this.validationErrors.push(
              this.$t(
                `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Number can not be 00000`,
                [i + 2, "Union Local"]
              )
            );
          }
          if (!this.isUniqueLocalNumber(i, locals)) {
            this.validationErrors.push(
              this.$t(
                `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Number must be unique per Union`,
                [i + 2, "Union Local"]
              )
            );
          }
        }
        if (
          locals[i].PhoneNumber !== null &&
          locals[i].PhoneNumber !== undefined
        ) {
          if (!this.isPhoneNumber(locals[i].PhoneNumber)) {
            this.validationErrors.push(
              this.$t(
                `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Telephone must be exactly 10 numeric digits`,
                [i + 2, "Union Local"]
              )
            );
          }
        }
        if (locals[i].Fax !== null && locals[i].Fax !== undefined) {
          if (!this.isPhoneNumber(locals[i].Fax)) {
            this.validationErrors.push(
              this.$t(
                `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Fax Number must be exactly 10 numeric digits`,
                [i + 2, "Union Local"]
              )
            );
          }
        }
        if (locals[i].Address !== null && locals[i].Address !== undefined) {
          (locals[i].Address = this.parseAddress(locals[i], i)), "Union Local";
        }
      }
      if (this.validationErrors.length === 0) {
        this.locals = locals.map((o) => {
          delete o.undefined;
          return o;
        });
      }
    },
    isUniqueLocalNumber(index, locals) {
      for (let i = 0; i < locals.length; i++) {
        if (index !== i) {
          if (
            locals[i].Number === locals[index].Number &&
            locals[i].UnionLocal === locals[index].UnionLocal
          ) {
            return false;
          }
        }
      }
      return true;
    },
    handleLocalsContactsChange(file) {
      if (!file) {
        this.file = null;
        return;
      }
      if (file.length == 0) {
        return;
      }
      this.onUploadStarted();
      var reader = new FileReader();
      reader.onload = async (event) => {
        var data = event.target.result;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(data);
        var worksheet = workbook.getWorksheet("Data");
        const headers = [];
        let contacts = [];
        worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          if (rowNumber === 1) {
            for (const v of row.values) {
              headers.push(this.unionLocalsContactsColumnsMatches[v]);
            }
          } else {
            const jsonRow = {
              Identifier: Math.random(),
            };
            for (let i = 0; i < row.values.length; i++) {
              if (headers[i] === "Email") {
                if (
                  typeof row.values[i] === "object" &&
                  row.values[i] !== null
                ) {
                  jsonRow[headers[i]] = row.values[i].text;
                } else {
                  jsonRow[headers[i]] = row.values[i];
                }
              } else {
                jsonRow[headers[i]] = row.values[i];
              }
            }
            contacts.push(jsonRow);
          }
        });
        this.validateLocalsContacts(contacts);
        if (this.validationErrors.length === 0) {
          this.assignContactsToLocals(
            contacts.map((o) => {
              delete o.undefined;
              return o;
            })
          );
        }
        this.onUploadCompleted();
      };
      reader.onerror = () => {
        this.onUploadError(reader.error);
      };
      reader.readAsBinaryString(file);
    },
    validateLocalsContacts(contacts) {
      this.validationErrors = [];
      for (let i = 0; i < contacts.length; i++) {
        const local = this.locals.filter(
          (o) => o.Name === contacts[i].UnionLocal
        );
        if (local.length === 0) {
          this.validationErrors.push(
            this.$t(
              `components.plan_builder.unions_import_wizard.errors.Row {0}: {1}'s Union Local does not exist`,
              [i + 2, "Contact"]
            )
          );
        }
        if (contacts[i].Name === null || contacts[i].Name === undefined) {
          this.validationErrors.push(
            this.$t(
              `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Name is required`,
              [i + 2, "Contact"]
            )
          );
        }
        if (contacts[i].Email !== null && contacts[i].Email !== undefined) {
          if (!this.isEmail(contacts[i].Email)) {
            this.validationErrors.push(
              this.$t(
                `components.plan_builder.employers_import_wizard.errors.Row {0}: {1}'s Email is not a valid email`,
                [i + 2, "Contact"]
              )
            );
          }
        }
      }
    },
    assignContactsToLocals(contacts) {
      for (let local of this.locals) {
        local.Contacts = contacts.filter((o) => o.UnionLocal === local.Name);
      }
    },
    assignLocalsToUnions() {
      for (let union of this.unions) {
        union.Locals = this.locals.filter((o) => o.Union === union.Name);
      }
    },
    finish() {
      this.assignLocalsToUnions();
      this.$emit("unions", this.unions);
      console.log(this.unions);
    },
    cancel() {
      this.dialog = false;
      this.$emit("close");
    },
    onUploadStarted() {
      this.uploaded = false;
      this.uploading = true;
    },
    onUploadCompleted(res) {
      this.uploaded = true;
      this.uploading = false;
      this.uploadResult = this.$t("globals.upload_successful");
    },
    onUploadError(err) {
      //console.log("ERR");
      //console.log(JSON.stringify(err));
      this.uploaded = true;
      this.uploading = false;
      this.error = err;
    },

    async downloadTemplate(filled) {
      return;
      this.downloading = true;
      const url =
        "plans/" +
        this.selectedPlan.Id +
        "/member-import" +
        (filled
          ? "/with-data/" + this.planRenewalDate
          : "/" + this.planRenewalDate);

      const filename = "Template.xlsx";
      try {
        let blob = (await this.getBlobAsync({ url: url, filename: filename }))
          .data;

        const url2 = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url2;
        link.setAttribute("download", filename); //or any other extension
        link.setAttribute("id", "link1"); //or any other extension
        document.body.appendChild(link);
        link.click();
        var element = document.getElementById("link1");
        element.parentNode.removeChild(element);
      } catch (error) {
        this.error = error;
      }
      this.downloading = false;
    },
  },
};
</script>
